/**
 * Supported UI languages. `code` is the i18next/CLDR language code; `name` is
 * the endonym (shown in the language switcher); `locale` is the BCP-47 tag used
 * for `Intl` number/date formatting; `dir` is the text direction.
 */
export interface LanguageDef {
  code: string;
  name: string;
  locale: string;
  dir: 'ltr' | 'rtl';
  /** Regional flag emoji shown in the language switcher. */
  flag: string;
}

export const LANGUAGES: LanguageDef[] = [
  { code: 'en', name: 'English', locale: 'en-US', dir: 'ltr', flag: '🇬🇧' },
  { code: 'ru', name: 'Русский', locale: 'ru-RU', dir: 'ltr', flag: '🇷🇺' },
  { code: 'es', name: 'Español', locale: 'es-ES', dir: 'ltr', flag: '🇪🇸' },
  { code: 'pt', name: 'Português', locale: 'pt-BR', dir: 'ltr', flag: '🇧🇷' },
  { code: 'fr', name: 'Français', locale: 'fr-FR', dir: 'ltr', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', locale: 'de-DE', dir: 'ltr', flag: '🇩🇪' },
  { code: 'tr', name: 'Türkçe', locale: 'tr-TR', dir: 'ltr', flag: '🇹🇷' },
  { code: 'id', name: 'Bahasa Indonesia', locale: 'id-ID', dir: 'ltr', flag: '🇮🇩' },
  { code: 'uk', name: 'Українська', locale: 'uk-UA', dir: 'ltr', flag: '🇺🇦' },
  { code: 'hi', name: 'हिन्दी', locale: 'hi-IN', dir: 'ltr', flag: '🇮🇳' },
  { code: 'zh', name: '中文', locale: 'zh-CN', dir: 'ltr', flag: '🇨🇳' },
  // Force Latin (Western) digits via the `-nu-latn` Unicode extension: the app's
  // amounts pair with Latin tickers ("GRAM"/"PRMD") and render inside gradient-
  // clipped text, where Arabic-Indic digits show up with broken spacing on some
  // clients. Separators and RTL direction stay native to Arabic.
  { code: 'ar', name: 'العربية', locale: 'ar-u-nu-latn', dir: 'rtl', flag: '🇸🇦' },
];

export const SUPPORTED_CODES = LANGUAGES.map((l) => l.code);
export const DEFAULT_LANG = 'en';

const byCode = new Map(LANGUAGES.map((l) => [l.code, l]));

/** Resolve a (possibly regional) language code to a supported {@link LanguageDef}. */
export function resolveLanguage(code: string | undefined | null): LanguageDef {
  if (!code) return byCode.get(DEFAULT_LANG)!;
  const base = code.toLowerCase().split('-')[0];
  return byCode.get(base) ?? byCode.get(DEFAULT_LANG)!;
}

/**
 * Grammatically-correct "ticket" noun for the active language. Russian and
 * Ukrainian use the Slavic 3-form rule (and the genitive singular for
 * fractional amounts); a few languages keep the noun invariant after numerals;
 * the rest use the simple one/other split.
 */
export function ticketNoun(lang: string, n: number): string {
  const base = lang.toLowerCase().split('-')[0];

  const slavic = (one: string, few: string, many: string): string => {
    if (!Number.isInteger(n)) return few; // genitive singular for fractions
    const mod100 = Math.abs(n) % 100;
    const mod10 = mod100 % 10;
    if (mod100 >= 11 && mod100 <= 14) return many;
    if (mod10 === 1) return one;
    if (mod10 >= 2 && mod10 <= 4) return few;
    return many;
  };
  const oneOther = (one: string, other: string): string =>
    Number.isInteger(n) && Math.abs(n) === 1 ? one : other;

  switch (base) {
    case 'ru':
      return slavic('билет', 'билета', 'билетов');
    case 'uk':
      return slavic('квиток', 'квитки', 'квитків');
    case 'es':
      return oneOther('boleto', 'boletos');
    case 'pt':
      return oneOther('bilhete', 'bilhetes');
    case 'fr':
      return oneOther('billet', 'billets');
    case 'de':
      return oneOther('Ticket', 'Tickets');
    case 'tr':
      return 'bilet'; // Turkish keeps the noun singular after numerals
    case 'id':
      return 'tiket';
    case 'zh':
      return '张彩票';
    case 'hi':
      return 'टिकट';
    case 'ar':
      return oneOther('تذكرة', 'تذاكر');
    case 'en':
    default:
      return oneOther('ticket', 'tickets');
  }
}
